import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import type { TooltipContentProps } from 'recharts/types/component/Tooltip';
import { formatDataLabel, formatTooltipValue } from './freeform-chart-artifact.helpers';

const CHART_HEIGHT = 280;

/**
 * Clickable legend shared by the XY charts (series) and the donut (slices):
 * one checkbox per item, toggling visibility via the id.
 *
 * @param props.items - Legend entries in render order.
 * @param props.visible - Visibility map keyed by item id; missing means visible.
 * @param props.onToggle - Called with the item id on click.
 * @returns The legend row.
 */
function SeriesLegend({
  items,
  visible,
  onToggle,
}: {
  items: { id: string; name: string; color: string }[];
  visible: Record<string, boolean>;
  onToggle: (id: string) => void;
}) {
  return (
    <LegendWrap>
      {items.map((item) => {
        const checked = visible[item.id] !== false;
        return (
          <LegendItem key={item.id} onClick={() => onToggle(item.id)} role="button" tabIndex={0}>
            <LegendCheckbox
              $checked={checked}
              style={{ borderColor: item.color, backgroundColor: checked ? item.color : 'transparent' }}
            >
              {checked && <Icon icon="material-symbols:check" width={10} height={10} />}
            </LegendCheckbox>
            <LegendLabel $dimmed={!checked}>{item.name}</LegendLabel>
          </LegendItem>
        );
      })}
    </LegendWrap>
  );
}

/**
 * Tooltip content for every freeform chart type: category header, one row per
 * visible series/slice, and an optional summed Total row for stacked charts.
 *
 * @param props - recharts tooltip content props plus `showTotal`.
 * @returns The tooltip body, or null while inactive.
 */
function ChartTooltip({
  active,
  payload: entries,
  label,
  showTotal,
}: TooltipContentProps<ValueType, NameType> & { showTotal?: boolean }) {
  if (!active || !entries?.length) return null;

  const total = entries.reduce((sum, entry) => sum + (typeof entry.value === 'number' ? entry.value : 0), 0);

  return (
    <TooltipContainer>
      {label != null && label !== '' && <TooltipLabel>{String(label)}</TooltipLabel>}
      {entries.map((entry) => (
        <TooltipRow key={String(entry.name)}>
          <TooltipSwatch style={{ backgroundColor: entry.color ?? (entry.payload as { fill?: string })?.fill }} />
          <TooltipName>{String(entry.name)}</TooltipName>
          <TooltipValue>{formatTooltipValue(entry.value)}</TooltipValue>
        </TooltipRow>
      ))}
      {showTotal && entries.length > 1 && (
        <TooltipTotalRow>
          <span>Total</span>
          <TooltipValue>{formatDataLabel(total)}</TooltipValue>
        </TooltipTotalRow>
      )}
    </TooltipContainer>
  );
}

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  color: var(--ink-lighter);
  font-size: 13px;
`;

/* ── Legend ── */

const LegendWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  padding: 8px 4px 0;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
  padding: 2px 4px;
  border-radius: 4px;
  transition: background 0.15s;
  /* let an unbroken token (URL, run id) wrap instead of clipping in the
     export screenshot */
  min-width: 0;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

const LegendCheckbox = styled.span<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1.5px solid;
  flex-shrink: 0;
  color: #fff;
  transition:
    background-color 0.15s,
    border-color 0.15s;
`;

const LegendLabel = styled.span<{ $dimmed: boolean }>`
  font-size: 12px;
  color: var(--ink-base);
  opacity: ${({ $dimmed }) => ($dimmed ? 0.4 : 1)};
  transition: opacity 0.15s;
  min-width: 0;
  overflow-wrap: anywhere;
`;

/* ── Tooltip ── */

const TooltipContainer = styled.div`
  background: var(--flora-chart-tooltip-bg);
  border: var(--flora-chart-tooltip-border);
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12px;
  color: var(--flora-chart-tooltip-text);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  /* an unbroken token (URL, run id) as the category would otherwise push the
     tooltip past a narrow panel's edge */
  overflow-wrap: anywhere;
`;

const TooltipLabel = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 1px 0;
`;

const TooltipSwatch = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
`;

// min-width lets the flex child shrink so an unbroken token wraps via the
// container's overflow-wrap instead of pushing the tooltip wide.
const TooltipName = styled.span`
  min-width: 0;
`;

const TooltipValue = styled.span`
  margin-left: auto;
  font-weight: 600;
`;

const TooltipTotalRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
  padding-top: 3px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  font-weight: 600;
`;

export { CHART_HEIGHT, ChartTooltip, EmptyState, SeriesLegend };
